import React from "react";
import { useDispatch } from "react-redux";
import { DEFAULT_TIMEZONE } from "repoV2/features/Common/modules/Timezone/Timezone.constants";
import { debugLogs } from "repoV2/features/Common/modules/DebugLogs/utils/Logs.utils";
import { getSystemTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { CURRENCY_STRINGS } from "repoV2/constants/payment";
import { logError } from "repoV2/utils/common/error/error";
import { useIpGeolocDetailsStore } from "repoV2/features/Common/modules/UserIp/UserIp.store";
import { callGetCheckoutPagePriceSectionApi } from "./apis/PriceDetailsApi/PriceDetailsApi";
import { CHECKOUT_PAGES_ACTION_TYPES } from "../constants/CheckoutPagesReducer";

const useFetchNSetCheckoutPagePriceSection = (props: {
    hostName: string;
    listingUuid: string;
    listingSlug: string;
    thankyouUuid: string;
    communityUuid: string;
    dynamicLinkUuid: string;
}) => {
    const {
        hostName,
        listingUuid,
        listingSlug,
        thankyouUuid,
        communityUuid,
        dynamicLinkUuid,
    } = props;

    const dispatch = useDispatch();

    /**
     * This function calls the price section data api, it is only called once
     * on client side on mount.
     */
    const getCheckoutPagePriceSectionData = async () => {
        try {
            // isCheckoutPagePriceSectionApiDataLoading is true by default
            // dont need to set it as true

            const data = await callGetCheckoutPagePriceSectionApi({
                hostName,
                listingSlug,
                thankyouUuid,
                communityUuid,
                dynamicLinkUuid,
            });

            /**
             * Check if the customer is an international one, incase it is then we set a prop
             * showInternationalInputFields as true, and will show international input fields in user form
             - Customer is considered international if they are at internaltional timezone or
             - The creator is international which would make the domestic customers as international
             * for the creator
             - The 'currency_name' is of non INR type - signifying that an international payment
             * is being made
             */
            const showInternationalInputFields =
                getSystemTimezone() !== DEFAULT_TIMEZONE ||
                data.is_international_creator ||
                data.currency_name !== CURRENCY_STRINGS.INR;

            /**
             * Check if the customer is international one and the creator
             * is domestic,
             * Also, incase customer is international, make sure currency is is non-inr
             * as payment-method paypal is not supported for INR payments,
             * if so we set showPaymentMethodInputField as true
             */
            const showPaymentMethodInputField = Boolean(
                data?.allow_payment_method_toggle
            );

            data.showInternationalInputFields = showInternationalInputFields;
            data.showPaymentMethodInputField = showPaymentMethodInputField;

            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_PRICE_SECTION_DATA,
                payload: {
                    data,
                    isFetched: true,
                    listingUuid,
                },
            });
        } catch (error) {
            debugLogs({
                data: {
                    call: "calling the checkout basic details, section 3 for custom questions",
                    hostName,
                    listingSlug,
                    href: window.location.href,
                    thankyouUuid,
                    communityUuid,
                    dynamicLinkUuid,
                },
                metaData: {},
            });
            logError({
                error,
                occuredAt:
                    "repoV2/features/CheckoutPages/hooks/useCheckoutPagePriceSection.ts",
                when: "when getCheckoutPagePriceSectionData function is called and price section api",
            });
        }
    };

    const ipGeolocDetailsUpdatedAt = useIpGeolocDetailsStore(
        state => state?.ipGeolocDetails?.metadata?.updatedAt
    );

    /**
     * We run 'getCheckoutPagePriceSectionData' on page mount to get the price related data on client side,
     * There is no check for listing status, as we need price data even when the listing status is expired or sold out
     * as we still send the price data on Post analytics lead event trigger, when user submits the 'Join Waitlist', or
     * 'Express Interest' form
     */
    React.useEffect(() => {
        if (ipGeolocDetailsUpdatedAt) {
            getCheckoutPagePriceSectionData();
        }
    }, [ipGeolocDetailsUpdatedAt]);
};

export default useFetchNSetCheckoutPagePriceSection;
